import React, { useState, useEffect, useCallback } from 'react';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────
interface CourseSession {
  id: number;
  courseId: number;
  orderIndex: number;
  title: string;
  content: string | null;
  output: string | null;
  homework: string | null;
  createdAt: string;
}

interface Props {
  courseId: number;
}

type FormMode = 'create' | 'edit';

const emptyForm = {
  title: '',
  orderIndex: 1,
  content: '',
  output: '',
  homework: ''
};

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────
const AdminCourseSessionManager: React.FC<Props> = ({ courseId }) => {
  const [sessions, setSessions] = useState<CourseSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formMode, setFormMode] = useState<FormMode>('create');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [showForm, setShowForm] = useState(false);

  // Admin app uses 'accessToken'
  const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/admin/courses/${courseId}/sessions`, { headers: getAuthHeader() });
      const data = await res.json();
      if (data.success) setSessions(data.data);
      else setError(data.message || 'Lỗi khi tải danh sách buổi học');
    } catch {
      setError('Không thể kết nối server');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleOpenCreate = () => {
    setFormMode('create');
    setEditingId(null);
    setForm({ ...emptyForm, orderIndex: sessions.length + 1 });
    setShowForm(true);
  };

  const handleOpenEdit = (session: CourseSession) => {
    setFormMode('edit');
    setEditingId(session.id);
    setForm({
      title: session.title,
      orderIndex: session.orderIndex,
      content: session.content || '',
      output: session.output || '',
      homework: session.homework || ''
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...emptyForm });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const url = formMode === 'create'
        ? `/api/v1/admin/courses/${courseId}/sessions`
        : `/api/v1/admin/sessions/${editingId}`;
      const method = formMode === 'create' ? 'POST' : 'PUT';

      const res = await fetch(url, {
        method,
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        await fetchSessions();
        handleCancel();
      } else {
        setError(data.message || 'Lỗi khi lưu');
      }
    } catch {
      setError('Không thể kết nối server');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sessionId: number, sessionTitle: string) => {
    if (!window.confirm(`Xóa buổi học "${sessionTitle}"?\nHành động này không thể hoàn tác.`)) return;
    try {
      const res = await fetch(`/api/v1/admin/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      const data = await res.json();
      if (data.success) {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
      } else {
        alert(data.message || 'Xóa thất bại');
      }
    } catch {
      alert('Không thể kết nối server');
    }
  };

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  return (
    <div className="bg-m2-surface-container rounded-xl shadow-sm border border-m2-outline-variant">
      {/* Header */}
      <div className="px-6 py-4 border-b border-m2-outline-variant flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-m2-on-surface">📚 Quản lý Buổi học</h2>
          <p className="text-sm text-m2-on-surface-variant mt-0.5">
            Nội dung chi tiết, Output và BTVN cho từng buổi của khóa học Online/Offline
          </p>
        </div>
        <button
          onClick={handleOpenCreate}
          className="flex items-center space-x-2 bg-m2-primary hover:bg-eh-forest-shadow text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors shadow-sm"
        >
          <span>＋</span>
          <span>Thêm Buổi học</span>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-6 mt-4 bg-m2-error/10 border border-m2-error/30 text-red-700 text-sm p-3 rounded-lg">
          ⚠️ {error}
        </div>
      )}

      {/* Form: Create / Edit */}
      {showForm && (
        <div className="mx-6 my-4 border border-m2-primary/30 bg-m2-primary/10 rounded-xl p-5">
          <h3 className="font-bold text-m2-on-surface mb-4">
            {formMode === 'create' ? '➕ Tạo buổi học mới' : `✏️ Chỉnh sửa: ${form.title}`}
          </h3>
          <form onSubmit={handleSave} className="space-y-4">
            {/* Row 1: orderIndex + title */}
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-m2-on-surface mb-1">Buổi số</label>
                <input
                  type="number"
                  min="1"
                  value={form.orderIndex}
                  onChange={e => setForm(f => ({ ...f, orderIndex: Number(e.target.value) }))}
                  className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
                  required
                />
              </div>
              <div className="col-span-10">
                <label className="block text-xs font-semibold text-m2-on-surface mb-1">
                  Tên buổi học <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="VD: Giới thiệu khóa học và tổng quan"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
                  required
                />
              </div>
            </div>

            {/* Content */}
            <div>
              <label className="block text-xs font-semibold text-m2-on-surface mb-1">
                📄 Nội dung chi tiết buổi học
              </label>
              <textarea
                rows={4}
                placeholder="Mô tả chi tiết những gì sẽ được học trong buổi này..."
                value={form.content}
                onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary resize-y"
              />
            </div>

            {/* Output */}
            <div>
              <label className="block text-xs font-semibold text-m2-on-surface mb-1">
                🎯 Output (Đầu ra / Mục tiêu buổi học)
              </label>
              <textarea
                rows={3}
                placeholder="Học viên sẽ đạt được gì sau buổi học này? VD: Hiểu được các khái niệm cơ bản..."
                value={form.output}
                onChange={e => setForm(f => ({ ...f, output: e.target.value }))}
                className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary resize-y"
              />
            </div>

            {/* Homework */}
            <div>
              <label className="block text-xs font-semibold text-m2-on-surface mb-1">
                📝 Bài tập về nhà (BTVN)
              </label>
              <textarea
                rows={3}
                placeholder="Mô tả bài tập về nhà cho học viên sau buổi học..."
                value={form.homework}
                onChange={e => setForm(f => ({ ...f, homework: e.target.value }))}
                className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary resize-y"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-3 pt-1">
              <button
                type="submit"
                disabled={saving}
                className="bg-m2-primary hover:bg-eh-forest-shadow text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? '⏳ Đang lưu...' : (formMode === 'create' ? 'Tạo buổi học' : 'Lưu thay đổi')}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="text-sm text-m2-on-surface-variant hover:text-m2-on-surface px-4 py-2 rounded-lg hover:bg-m2-surface-high transition-colors"
              >
                Hủy
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sessions List */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-10 text-m2-outline">
            <div className="inline-block w-6 h-6 border-2 border-m2-outline border-t-blue-600 rounded-full animate-spin mb-2" />
            <p className="text-sm">Đang tải...</p>
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-m2-outline-variant rounded-xl">
            <div className="text-5xl mb-3">📅</div>
            <h3 className="font-semibold text-m2-on-surface mb-1">Chưa có buổi học nào</h3>
            <p className="text-sm text-m2-on-surface-variant mb-4">
              Nhấn "Thêm Buổi học" để tạo nội dung học cho từng buổi
            </p>
            <button
              onClick={handleOpenCreate}
              className="bg-m2-primary hover:bg-eh-forest-shadow text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors"
            >
              ＋ Tạo buổi học đầu tiên
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.map(session => (
              <div
                key={session.id}
                className="border border-m2-outline-variant rounded-xl p-4 hover:border-m2-primary/40 hover:bg-m2-primary/10/30 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    {/* Order Badge */}
                    <div className="w-9 h-9 bg-m2-primary/15 text-m2-primary rounded-lg flex items-center justify-center font-bold text-sm shrink-0">
                      {session.orderIndex}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-m2-on-surface truncate">{session.title}</h4>
                      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-m2-on-surface-variant">
                        {session.content && (
                          <span className="flex items-center gap-1"><span>📄</span><span>Có nội dung</span></span>
                        )}
                        {session.output && (
                          <span className="flex items-center gap-1"><span>🎯</span><span>Có output</span></span>
                        )}
                        {session.homework && (
                          <span className="flex items-center gap-1"><span>📝</span><span>Có BTVN</span></span>
                        )}
                        {!session.content && !session.output && !session.homework && (
                          <span className="text-m2-outline italic">Chưa có nội dung chi tiết</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="flex items-center space-x-2 shrink-0">
                    <button
                      onClick={() => handleOpenEdit(session)}
                      className="text-xs text-m2-primary hover:text-m2-primary bg-m2-primary/10 hover:bg-m2-primary/15 px-3 py-1.5 rounded-lg font-medium transition-colors"
                    >
                      ✏️ Sửa
                    </button>
                    <button
                      onClick={() => handleDelete(session.id, session.title)}
                      className="text-xs text-red-600 hover:text-m2-error bg-m2-error/10 hover:bg-red-100 px-3 py-1.5 rounded-lg font-medium transition-colors"
                    >
                      🗑️ Xóa
                    </button>
                  </div>
                </div>

                {/* Preview content */}
                {(session.content || session.output || session.homework) && (
                  <div className="mt-3 pl-12 grid grid-cols-1 md:grid-cols-3 gap-3">
                    {session.content && (
                      <div className="bg-m2-surface-container border border-m2-outline-variant rounded-lg p-3">
                        <p className="text-[10px] font-bold text-m2-outline uppercase tracking-wider mb-1">📄 Nội dung</p>
                        <p className="text-xs text-m2-on-surface-variant line-clamp-2">{session.content}</p>
                      </div>
                    )}
                    {session.output && (
                      <div className="bg-m2-surface-container border border-m2-outline-variant rounded-lg p-3">
                        <p className="text-[10px] font-bold text-green-500 uppercase tracking-wider mb-1">🎯 Output</p>
                        <p className="text-xs text-m2-on-surface-variant line-clamp-2">{session.output}</p>
                      </div>
                    )}
                    {session.homework && (
                      <div className="bg-m2-surface-container border border-m2-outline-variant rounded-lg p-3">
                        <p className="text-[10px] font-bold text-orange-500 uppercase tracking-wider mb-1">📝 BTVN</p>
                        <p className="text-xs text-m2-on-surface-variant line-clamp-2">{session.homework}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCourseSessionManager;
